import React, {createContext, DependencyList, ReactNode, useCallback, useContext, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {SetState} from "../../util/FormHandlers";
import {message as msg} from "../../util/actions/ActionUtil";
import {useMessage} from "../../util/actions/Message";
import {apiErrorMessage} from "../../util/ApiErrorMessage";


interface FormDialogContextType {
    openFormDialog: <Form>(options: FormDialogOptions<Form>, action: FormDialogAction<Form>) => void;
}

// Create the context with a default value
const FormDialogContext = createContext<FormDialogContextType | null>(null);

interface FormDialogRendererContextType {
    dialogState: DialogState | null;
    form: any;
    setForm: SetState<any>;
    loading: boolean;
    error?: string;
    handleClose: (e: React.FormEvent) => void;
    handleSubmit: (e: React.FormEvent) => Promise<void>;
}

const FormDialogRendererContext = createContext<FormDialogRendererContextType | null>(null);

export interface FormDialogOptions<Form> {
    title: string;
    description: ReactNode;
    fullWidth?: boolean;
    submitButtonText: string;
    extraButtons?: React.ComponentType<{
        form: Form,
        setForm: SetState<Form>,
    }>;
    renderForm: React.ComponentType<{
        form: Form,
        setForm: SetState<Form>,
    }>;
}

interface FormDialogAction<Form> {
    initialValue: Form;
    onSubmit: (form: Form) => Promise<any>;
    successMessage: string | ((form: Form) => string);
    errorMessage: string | ((form: Form) => string);
}


// Internal state for the dialog
interface DialogState<Form = any> {
    isOpen: boolean;
    title: string;
    description: ReactNode;
    fullWidth: boolean;
    submitButtonText: string;
    extraButtons?: React.ComponentType<{
        form: Form,
        setForm: SetState<Form>,
    }>;
    onSubmit: (form: Form) => Promise<any>,
    successMessage: string | ((form: Form) => string),
    errorMessage: string | ((form: Form) => string),
    renderForm: React.ComponentType<{
        form: Form,
        setForm: SetState<Form>,
    }>;
}

export function FormDialogProvider({children}: {
    children: ReactNode,
}) {
    // State to manage the dialog
    const [dialogState, setDialogState] = useState<DialogState | null>(null);
    const [form, setForm] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const message = useMessage();

    // Function to open the dialog
    const openFormDialog = useCallback(<Form, >(options: FormDialogOptions<Form>, action: FormDialogAction<Form>) => {
        setForm(action.initialValue);
        setError(undefined);
        setDialogState({
            isOpen: true,
            title: options.title,
            description: options.description,
            fullWidth: options.fullWidth ?? false,
            submitButtonText: options.submitButtonText,
            extraButtons: options.extraButtons,
            renderForm: options.renderForm,
            onSubmit: action.onSubmit,
            successMessage: action.successMessage,
            errorMessage: action.errorMessage,
        });
    }, [setDialogState]);

    // Function to handle dialog close
    const handleClose = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        setDialogState(null);
    }, [setDialogState]);

    // Function to handle form submission
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (dialogState) {
            setLoading(true);
            setError(undefined);
            try {
                await dialogState.onSubmit(form);
                message.success(msg(dialogState.successMessage, form));
                setDialogState(null);
                setForm(null);
            } catch (e) {
                setError(apiErrorMessage(e) || msg(dialogState.errorMessage, form));
            } finally {
                setLoading(false);
            }
        }
    }, [form, dialogState, setDialogState, message]);

    const contextValue = useMemo(() => ({openFormDialog}), [openFormDialog]);
    const rendererContextValue = useMemo(() => ({
        dialogState,
        form,
        setForm,
        loading,
        error,
        handleClose,
        handleSubmit,
    }), [dialogState, form, loading, error, handleClose, handleSubmit]);

    return (
        <FormDialogContext value={contextValue}>
            <FormDialogRendererContext value={rendererContextValue}>
                {children}
            </FormDialogRendererContext>
        </FormDialogContext>
    );
}

export function FormDialogRenderer() {
    const context = useContext(FormDialogRendererContext);
    if (!context) {
        throw new Error('FormDialogRenderer must be used within a FormDialogProvider');
    }
    const {dialogState, form, setForm, loading, error, handleClose, handleSubmit} = context;
    if (!dialogState) return null;

    const FormComponent = dialogState.renderForm;
    const ExtraButtons = dialogState.extraButtons;

    return createPortal(
        <Dialog open={dialogState.isOpen} onClose={handleClose} fullWidth={dialogState.fullWidth} maxWidth="md">
            <Box component="form" onSubmit={handleSubmit}>
                <DialogTitle>{dialogState.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        {dialogState.description}
                    </DialogContentText>
                    {FormComponent && form && (
                        <FormComponent form={form} setForm={setForm}/>
                    )}
                    {error && (
                        <Alert severity="error" sx={{mt: 2, mb: 2}}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{px: 3, py: 2}}>
                    <Button onClick={handleClose}>Cancel</Button>
                    {ExtraButtons && (
                        <ExtraButtons form={form} setForm={setForm}/>
                    )}
                    <Button type="submit" variant="contained" color="primary" loading={loading}>
                        {dialogState.submitButtonText}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>,
        document.body,
    );
}

export function useFormDialogAction<Form, Params extends any[]>({action, ...options}: FormDialogOptions<Form> & {
    action: (openDialog: (p: FormDialogAction<Form>) => void, ...params: Params) => void,
}, depList: DependencyList): (...params: Params) => Promise<void> {
    const context = useContext(FormDialogContext);
    if (!context) {
        throw new Error('useFormDialogAction must be used within a FormDialogProvider');
    }

    /* eslint-disable react-hooks/exhaustive-deps */
    return useCallback(async (...params: Params) => {
        return action(
            (formDialogAction: FormDialogAction<Form>) => context.openFormDialog<Form>(options, formDialogAction),
            ...params,
        );
    }, depList);
    /* eslint-enable react-hooks/exhaustive-deps */
}
